// @ts-check
// Editor dos campos medidos da qualidade do lead (contrato 07, seção 13.6), no painel lateral do widget: caixas por
// cadastro (lead, família, responsável e alunos), selo "obrigatório no funil" nas medidas cuja regra de etapa está
// ligada, aviso das medidas que só valem a partir de uma etapa e das que o papel de quem edita não lê, "Restaurar
// padrão" e "Aplicar a todos os cartões de qualidade do painel" (marcado por padrão).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Checkbox } from '../../ui/feedback.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { fetchStageRequirements } from '../../data/stage-requirements.js';
import { canReadObjectRecords } from '../../lib/permissions.js';
import { normalizeMeasureSelection, selectedMeasureKeys, toggleMeasure } from '../../lib/lead-quality-draft.js';
import { LEAD_QUALITY_ENTITIES, LEAD_QUALITY_MEASURES, RULE_STAGES } from '../../../../shared/dashboards/lead-quality-measures.js';
import { setDraftLeadQualityMeasures } from './dashboard-state.js';
import { leadQualityMeasureLabel } from './report-widgets.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../../../shared/dashboards/lead-quality-measures.js').LeadQualityMeasure} LeadQualityMeasure */

/** Regras de etapa ligadas na escola (a mesma tela de Configurações > Funil e etapas), lidas uma vez por sessão. */
/** @type {Promise<Set<string>> | null} */
let enabledRequirementsPromise = null;
function loadEnabledRequirements() {
  enabledRequirementsPromise ??= fetchStageRequirements()
    .then((result) => new Set(result.stageRequirements.filter((requirement) => requirement.isEnabled).map((requirement) => requirement.key)))
    .catch(() => {
      enabledRequirementsPromise = null;
      return new Set();
    });
  return enabledRequirementsPromise;
}

/**
 * O papel de quem edita lê todos os campos obrigatórios da medida? (a API decide de verdade; aqui é só o aviso)
 * @param {LeadQualityMeasure} measure
 */
function isMeasureReadable(measure) {
  return measure.requiredFields.every(([objectName, fieldName]) => {
    const objectMetadata = objectsByNameSingular.value.get(objectName);
    return Boolean(objectMetadata) && canReadObjectRecords(objectMetadata) && objectMetadata.fields.some((field) => field.name === fieldName);
  });
}

/** @param {string} stage */
function stageLabel(stage) {
  const option = objectsByNameSingular.value.get('enrollmentOpportunity')?.fields.find((field) => field.name === 'stage')?.options?.find((candidate) => candidate.value === stage);
  return option?.label ?? stage;
}

/** @param {LeadQualityMeasure} measure */
function conditionText(measure) {
  if (measure.rule === 'LOST') return t('reports.leadQualityEditor.conditionalLost');
  if (measure.rule === 'VISIT_PASSED') return t('reports.leadQualityEditor.conditionalVisitPassed');
  if (measure.rule) return t('reports.leadQualityEditor.conditional', { stage: stageLabel(/** @type {string} */ (RULE_STAGES[measure.rule])) });
  if (measure.needsHousehold) return t('reports.leadQualityEditor.needsHousehold');
  return null;
}

/**
 * @param {{ widgetKey: string, configuration: Record<string, any> }} props
 */
export function LeadQualityMeasuresEditor({ widgetKey, configuration }) {
  const [applyToAll, setApplyToAll] = useState(true);
  const [enabledRequirements, setEnabledRequirements] = useState(/** @type {Set<string>} */ (new Set()));
  useEffect(() => {
    let isCancelled = false;
    loadEnabledRequirements().then((keys) => {
      if (!isCancelled) setEnabledRequirements(keys);
    });
    return () => {
      isCancelled = true;
    };
  }, []);
  const selected = selectedMeasureKeys(configuration);
  const save = (/** @type {string[] | undefined} */ measures) => setDraftLeadQualityMeasures(widgetKey, measures, applyToAll);
  return html`<div class="lead-quality-editor">
    <p class="widget-editor-hint">${t('reports.leadQualityEditor.hint')}</p>
    ${LEAD_QUALITY_ENTITIES.map((entity) => html`<fieldset class="lead-quality-editor-group" key=${entity}>
      <legend class="form-field-label">${t(`reports.leadQualityEntities.${entity}`)}</legend>
      ${LEAD_QUALITY_MEASURES.filter((measure) => measure.entity === entity).map((measure) => {
        const isChecked = selected.includes(measure.key);
        const condition = conditionText(measure);
        const isRequired = Boolean(measure.stageRequirementKey && enabledRequirements.has(measure.stageRequirementKey));
        const isReadable = isMeasureReadable(measure);
        // a caixa já é um label: a linha é um div para não aninhar labels
        return html`<div class="lead-quality-editor-row" key=${measure.key} data-unreadable=${isReadable ? undefined : 'true'}>
          <${Checkbox} checked=${isChecked} ariaLabel=${leadQualityMeasureLabel(measure.key)} disabled=${isChecked && selected.length === 1}
            onChange=${() => save(normalizeMeasureSelection(toggleMeasure(selected, measure.key)))} />
          <span class="lead-quality-editor-text">
            <span>${leadQualityMeasureLabel(measure.key)}</span>
            ${isRequired ? html`<span class="lead-quality-editor-badge">${t('reports.leadQualityEditor.stageRequirementBadge')}</span>` : null}
            ${condition ? html`<span class="lead-quality-editor-note">${condition}</span>` : null}
            ${isReadable ? null : html`<span class="lead-quality-editor-note" data-tone="danger"><${Icon} name="IconLock" size=${12} />${t('reports.leadQualityEditor.unreadable')}</span>`}
          </span>
        </div>`;
      })}
    </fieldset>`)}
    <p class="widget-editor-hint">${t('reports.leadQualityEditor.selected', { count: selected.length })}${selected.length === 1 ? ` · ${t('reports.leadQualityEditor.minimum')}` : ''}</p>
    <div class="lead-quality-editor-row">
      <${Checkbox} checked=${applyToAll} ariaLabel=${t('reports.leadQualityEditor.applyToAll')} onChange=${() => setApplyToAll(!applyToAll)} />
      <span class="lead-quality-editor-text">${t('reports.leadQualityEditor.applyToAll')}</span>
    </div>
    <div>
      <${Button} title=${t('reports.leadQualityEditor.restoreDefault')} icon="IconRestore" size="small" variant="secondary"
        disabled=${!Array.isArray(configuration.measures)} onClick=${() => save(undefined)} />
    </div>
  </div>`;
}
